<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Courses
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="`View courses of each program.`"
          >info</i
        >
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'create-course' }">
          Add Course
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search Courses" v-model="searchTerm" @input="onSearch" />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <div v-if="!isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="row mx-auto mb-2  w-100 mt-2">
                  <div class="col-md-6">
                    <v-select
                      id="prog_category"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="program_category"
                      :reduce="pg => pg.id"
                      placeholder="Select Program Category"
                      :options="allPrograms"
                      :loading="areProgramsLoading"
                      @input="fetchAllCourses"
                    >
                    </v-select>
                  </div>
                </div>
              </div>
              <div v-if="isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="mb-2 mt-2" style="width: 100%;">
                  <div class="mb-2">
                    <v-select
                      id="prog_category"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="program_category"
                      :reduce="pg => pg.id"
                      placeholder="Select Program Category"
                      :options="allPrograms"
                      :loading="areProgramsLoading"
                      @input="fetchAllCourses"
                    >
                    </v-select>
                  </div>
                </div>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="courses"
              v-if="courses.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
              @row-clicked="rowClicked"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(image_url)="data">
                <div class="text-center">
                  <user-avatar slot="aside" size="md" :fileUrl="data.item.image_url"> </user-avatar>
                </div>
              </template>

              <template #cell(title)="data"
                ><strong> {{ data.item.title }}</strong></template
              >

              <template #cell(program_category)="data">
                <div class="text-center">{{ data.item.program_category.title }}</div>
              </template>
              <template #cell(is_published)="data">
                <md-icon v-if="data.item.is_published" class="text-success">done</md-icon>
                <md-icon v-else class="text-danger">close</md-icon>
                <!-- <div class="text-center">{{ data.item.is_published }}</div> -->
              </template>
              <template #cell(actions)="data">
                <router-link
                  :to="{
                    name: 'edit-lms-course',
                    params: { id: data.item.id },
                  }"
                  class="svg-icon mr-2"
                  v-b-popover.hover.left
                  :title="$t('generalMsgs.edit')"
                >
                  <md-icon class="text-info">edit</md-icon>
                  <!-- <i class="far fa-edit text-info" /> -->
                </router-link>

                <a
                  href="#"
                  @click.prevent="confirmDeleteCourse(data.item.id)"
                  v-b-popover.hover.left
                  :title="$t('generalMsgs.remove')"
                >
                  <md-icon class="text-danger">delete</md-icon>
                </a>
              </template>
            </b-table>
            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalCourses"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="courses-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import UserAvatar from '../../../components/User/UserAvatar.vue';
import { formatFullDate } from '@/common/utils';
import Pagination from '../../../components/Ui/Pagination.vue';

export default {
  components: {
    UserAvatar,
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'View Courses',
      date: null,
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalCourses: 0,
      searchTerm: null,
      program: null,
      allPrograms: [],
      program_category: '',
      areProgramsLoading: false,
      windowWidth: window.innerWidth,
      courses: [],
      limit: 15,
      offset: 0,
      search: '',
    };
  },

  watch: {
    program: {
      handler() {
        this.offset = 0;
        this.search = '';
      },
    },
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    tableColumns() {
      return [
        { key: 'image_url', label: '' },
        { key: 'title', label: 'Title' },
        { key: 'program_category', label: 'Program' },
        { key: 'is_published', label: 'Published', class: 'text-center' },
        {
          key: 'created_at',
          label: 'Created At',
          tdClass: 'clickable-item',
          formatter: value => (value ? formatFullDate(value) : value),
        },
        {
          key: 'updated_at',
          label: 'Updated At',
          tdClass: 'clickable-item',
          formatter: value => (value ? formatFullDate(value) : value),
        },
        {
          key: 'actions',
          tdClass: 'text-right',
          thStyle: { minWidth: '100px' },
        },
      ];
    },

    isTabSmallScreen() {
      return this.windowWidth <= 767;
    },
  },

  methods: {
    get,
    ...mapActions('courses', ['getLmsCourses', 'deleteCourse']),
    ...mapActions('program', ['getAllPrograms']),

    clearFilters() {
      this.program = null;
      this.tag_id = null;
    },
    dateChange(value) {
      if (value[0]) {
        this.date = value;
      } else {
        this.date = null;
      }
    },
    onPageChange(pageNum) {
      this.offset = (pageNum - 1) * this.perPage;
      this.fetchAllCourses();
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },
    async confirmDeleteCourse(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete the Course?', {
          title: 'Delete Course',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'success',
          cancelVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          await this.onDeleteCourse(id);
          this.fetchAllCourses();
          // const refreshPage = this.careers.length > 1 ? this.currentPage : this.currentPage - 1;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onDeleteCourse(id) {
      try {
        await this.deleteCourse(id);
        this.makeToast({ variant: 'success', msg: 'Course Successfully Deleted' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async fetchAllCourses(params = {}) {
      this.isLoading = true;
      try {
        const res = await this.getLmsCourses({
          school__id: this.getLoggedInUser.linked_entity.id,
          limit: this.perPage,
          offset: this.offset,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.program_category ? { program_category__id: this.program_category } : {}),
          pagination: true,
          ...params,
        });

        this.courses = res.data.results;
        this.totalCourses = res.data.count;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    rowClicked(row) {
      this.$router.push({
        name: 'edit-lms-course',
        params: { id: row.id },
      });
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchStudents();
    },

    onSearch() {
      this.offset = 0; // Reset offset when searching
      this.debouncedCourseSearch(this);
    },

    debouncedCourseSearch: debounce(vm => {
      vm.fetchAllCourses();
    }, 500),

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },

  async mounted() {
    await this.fetchPrograms();
    await this.fetchAllCourses();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped lang="scss">
.loader {
  text-align: center;
  color: #bbbbbb;
  margin-inline: auto;
}
</style>
